import type {
  InjuryStatuses,
  ModifiedPlayer,
  NFLPlayerInputs,
  OddsResponse,
  ProvidedOverride
} from '@library/types/simulations';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import type { SelectOption, SelectOptions } from '@library/types/utils';
import {
  filterDefaultPlayerInputsByPosition,
  formatAmericanOdds
} from '@bo-utils/simulations';
import { useMemo, useState } from 'react';
import { BooleanOverride } from '@bo-components/simulations/steps/StepOneBuild/PlayerInputs/inputs/BooleanOverride';
import type { LeagueTeamPlayer } from '@library/types/player';
import { NumberOverride } from '@bo-components/simulations/steps/StepOneBuild/PlayerInputs/inputs/PlayerNumberOverride';
import type { OverrideValidationError } from '@bo-hooks/simulations/useSimulationDetailSteps';
import { Select } from '@library/components/selects';
import { StatusIndicator } from '@library/components/StatusIndicator';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import type { Updater } from 'use-immer';

export const PlayerInputs = ({
  defaultPlayers,
  homeTeam,
  injury = undefined,
  modifiedPlayers,
  simulationId,
  simulationOdds,
  overrideValidationErrors,
  setModifiedPlayers,
  setOverrideValidationErrors,
  player
}: {
  defaultPlayers: Array<ModifiedPlayer>;
  homeTeam: boolean;
  injury?: {
    player: {
      id: string;
      first_name: string;
      last_name: string;
      platoon: string;
      team_id: string;
    };
    injuries: Array<{
      id: string;
      status: InjuryStatuses;
      type: string;
      location: string;
      details: string;
      side: string;
      return_date: string;
      sources: Array<string>;
      created_at: string;
      updated_at: string;
    }>;
  };
  modifiedPlayers: Array<ModifiedPlayer>;
  simulationId: string;
  simulationOdds: OddsResponse | undefined;
  overrideValidationErrors: Array<OverrideValidationError>;
  setModifiedPlayers: Updater<Array<ModifiedPlayer>>;
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
  player: LeagueTeamPlayer;
}) => {
  const [defaultInputs, optionalInputs] = filterDefaultPlayerInputsByPosition({
    player
  });

  const [additionalInputs, setAdditionalInputs] = useState<
    Array<ProvidedOverride<keyof NFLPlayerInputs>>
  >([]);

  const addedInputKeys = useMemo(
    () => additionalInputs.map((input) => input.value),
    [additionalInputs]
  );

  const additionalInputDropdownOptions: SelectOptions = useMemo(() => {
    const remainingInputDropdownOptions: SelectOptions = [];
    optionalInputs.forEach((input) => {
      if (!addedInputKeys.includes(input.value)) {
        remainingInputDropdownOptions.push({
          key: input.value,
          name: input.label,
          value: input.value
        });
      }
    });
    return remainingInputDropdownOptions;
  }, [addedInputKeys, optionalInputs]);

  const handleAddInput = (input: SelectOption<string>) => {
    const newInput = optionalInputs.find(
      (override) => override.value === input.value
    );
    if (newInput) {
      setAdditionalInputs([...additionalInputs, newInput]);
    }
  };

  const { modifiedPlayer, modifiedPlayerIndex } = useMemo(() => {
    const index = modifiedPlayers.findIndex(
      (currentModifiedPlayer) =>
        currentModifiedPlayer.foreignId === player.foreign_id
    );

    return {
      modifiedPlayer: modifiedPlayers[index],
      modifiedPlayerIndex: index
    };
  }, [modifiedPlayers, player.foreign_id]);

  const defaultPlayer = useMemo(
    () =>
      defaultPlayers.find(
        (currentDefaultPlayer) =>
          currentDefaultPlayer.foreignId === player.foreign_id
      ),
    [defaultPlayers, player.foreign_id]
  );

  const handleModifyPlayer = ({
    playerName,
    value
  }: {
    playerName: string;
    value: Partial<NFLPlayerInputs>;
  }) => {
    setModifiedPlayers((draft) => {
      if (!modifiedPlayer) {
        draft.push({
          foreignId: player.foreign_id,
          homeTeam,
          playerId: player.foreign_id,
          playerName,
          playerOverrides: value
        });
      } else {
        const existingModifiedPlayer = draft[modifiedPlayerIndex];

        existingModifiedPlayer.playerOverrides = {
          ...existingModifiedPlayer.playerOverrides,
          ...value
        };
      }
    });
  };

  // TODO - Change Player name to player id when it is confirmed that the player id is reliable
  const simOddsPlayer = simulationOdds?.players.find(
    (oddsPlayer) =>
      oddsPlayer.player_name.toLocaleUpperCase() ===
      player.name.toLocaleUpperCase()
  );

  const showLinePeek =
    simOddsPlayer &&
    simOddsPlayer.markets.length > 0 &&
    simOddsPlayer.markets[0].median_lines.simulation_runs.find(
      (simRun) => simRun.simulation_run_id === simulationId
    );

  const markets =
    (showLinePeek &&
      simOddsPlayer?.markets.map((playerMarket) => {
        const { market, median_lines: medianLines } = playerMarket;
        const simulationRun = medianLines.simulation_runs[0];
        const { line, odds } = simulationRun;
        const { american_odds: americanOdds } = odds.devigged;
        const formattedAmericanOdds = formatAmericanOdds(americanOdds);

        return `${market}: ${line} (${formattedAmericanOdds})`;
      })) ||
    '';

  return (
    <>
      <div className="bg-soFresh-110 sticky left-0 z-10 h-16 w-60 self-start px-4">
        <Tooltip text={markets} title={`Line Peek - ${player.name}`}>
          <PlayerProfile
            className="flex h-full min-w-48 shrink-0 basis-52 items-center gap-4 self-stretch"
            name={player.name}
            imageUrl={player.image_url}
            subheader={`${player.position} • ${player.team_name}`}
          >
            <PlayerImage className="bg-soFresh-120 size-[49px] rounded-lg" />
            <div className="flex flex-col">
              <div className="flex flex-row">
                <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium leading-tight tracking-tight" />
                {injury && (
                  <StatusIndicator
                    className="pl-4 pt-1"
                    status={injury.injuries[0].status}
                  />
                )}
              </div>
              <PlayerSubheader className="text-soClean-160 font-heebo block text-xs font-medium leading-[19px] tracking-tight" />
            </div>
          </PlayerProfile>
        </Tooltip>
      </div>
      <div className="flex h-16 grow flex-row self-center">
        {defaultInputs.map((playerOverride) => (
          <div
            className="flex w-48 flex-col items-center gap-1"
            key={playerOverride.value}
          >
            <Tooltip
              text={playerOverride.description}
              title={playerOverride.value}
            >
              <span className="font-heebo text-xs font-medium">
                {playerOverride.label}
              </span>
            </Tooltip>

            {playerOverride.type === 'bool' && (
              <BooleanOverride
                field={playerOverride.value}
                homeTeam={homeTeam}
                modifiedPlayer={modifiedPlayer}
                onModifyPlayer={handleModifyPlayer}
                player={player}
              />
            )}

            {playerOverride.type === 'number' &&
              playerOverride.format === 'float' && (
                <NumberOverride
                  defaultPlayer={defaultPlayer}
                  field={playerOverride.value}
                  homeTeam={homeTeam}
                  max={playerOverride.maximum}
                  min={playerOverride.minimum}
                  modifiedPlayer={modifiedPlayer}
                  overrideValidationErrors={overrideValidationErrors}
                  onModifyPlayer={handleModifyPlayer}
                  setOverrideValidationErrors={setOverrideValidationErrors}
                  player={player}
                />
              )}
          </div>
        ))}

        {additionalInputs.map((playerOverride) => (
          <div
            className="flex w-48 flex-col items-center gap-1"
            key={playerOverride.value}
          >
            <Tooltip
              text={playerOverride.description}
              title={playerOverride.value}
            >
              <span className="font-heebo text-xs font-medium">
                {playerOverride.label}
              </span>
            </Tooltip>

            {playerOverride.type === 'bool' && (
              <BooleanOverride
                field={playerOverride.value}
                homeTeam={homeTeam}
                modifiedPlayer={modifiedPlayer}
                onModifyPlayer={handleModifyPlayer}
                player={player}
              />
            )}

            {playerOverride.type === 'number' &&
              playerOverride.format === 'float' && (
                <NumberOverride
                  defaultPlayer={defaultPlayer}
                  field={playerOverride.value}
                  homeTeam={homeTeam}
                  modifiedPlayer={modifiedPlayer}
                  onModifyPlayer={handleModifyPlayer}
                  overrideValidationErrors={overrideValidationErrors}
                  setOverrideValidationErrors={setOverrideValidationErrors}
                  player={player}
                />
              )}
          </div>
        ))}

        {additionalInputDropdownOptions.length > 0 && (
          <div className="flex w-48 flex-col items-center gap-1" key={1}>
            <Tooltip
              text="Add an additional input type for this player"
              title="Add Input"
            >
              <span className="font-heebo text-xs font-medium">Add Input:</span>
            </Tooltip>
            <Select
              size="small"
              options={additionalInputDropdownOptions}
              label="input options"
              value=""
              onChange={handleAddInput}
              className="font-heebo text-soClean-100 disabled:text-soClean-160 border-soFresh-140 !h-8 !min-h-[32px] w-40 rounded-md bg-transparent px-0 text-center text-sm font-medium leading-5 tracking-tight"
            />
          </div>
        )}
      </div>
    </>
  );
};
